import React, { useState, useRef, useEffect } from 'react';
import './LearnPage.css';
import { useNavigate, useLocation } from 'react-router-dom';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

import NavigationButtons from '../components/NavigationButtons';
import PokoViewer from '../components/PokoViewer';
import poko_read from '../assets/poko_read.PNG';

// Map of common words to their single-action animations
const commonWordAnimations = {
	'hello': 'Word Hello',
	'goodbye': 'Word Bye',
	'howareyou': 'Word How are you',
	'goodnight': 'Word Good Night',

	'thanks': 'Word Thanks',
	'please': 'Word Please',
	'sorry': 'Word Sorry',

	'happy': 'Word Happy',
	'sad': 'Word Sad',
	'smile': 'Word Smile',
	'like': 'Word Like',
	'bad': 'Word Bad',

	'love': 'Word Love',
	'iloveyou': 'Word Love',

	'me': 'Word Me',
	'you': 'Word You',
	'teacher': 'Word Teacher',

	'good': 'Word Good',
	'goodluck': 'Word Good Luck',
	'yes': 'Word Yes',
	'no': 'Word No',
	'stop': 'Word Stop',
	'come': 'Word Come',

	'bed': 'Word Bed',
	'see': 'Word See',
	'have': 'Word Have'
};

// Helper function to check if a word has a single-action animation
const hasCommonWordAnimation = (word) => {
	const normalizedInput = word.toLowerCase().replace(/\s/g, '');

	for (const key of Object.keys(commonWordAnimations)) {
		const normalizedKey = key.toLowerCase().replace(/\s/g, '');
		if (normalizedKey === normalizedInput) {
			return true;
		}
	}

	return false;
};

// Helper function to get the animation for a common word
const getCommonWordAnimation = (word) => {
	const normalizedInput = word.toLowerCase().replace(/\s/g, '');

	for (const [key, value] of Object.entries(commonWordAnimations)) {
		const normalizedKey = key.toLowerCase().replace(/\s/g, '');
		if (normalizedKey === normalizedInput) {
			return value;
		}
	}
	return null;
};


// Generate an array of string numbers from '0' to '10'
const availableNumbers = Array.from({ length: 11 }, (_, i) => i.toString());

// Generate an array of uppercase letters from 'A' to 'Z'
const availableLetters = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

// Import all sign images (letters, numbers, and common signs) from asset folders
function importAllSigns() {
	const letterCtx = require.context('../assets/signs/letter', false, /\.(png|jpe?g|svg)$/);
	const numberCtx = require.context('../assets/signs/number', false, /\.(png|jpe?g|svg)$/);
	const commonCtx = require.context('../assets/signs/commonSign', false, /\.(png|jpe?g|svg)$/);

	// Helper function to extract file names and map them to their imported images
	const importSet = (ctx) => {
		const images = {};
		ctx.keys().forEach((key) => {
			const fileName = key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '');
			images[fileName] = ctx(key);
		});
		return images;
	};

	return {
		letters: importSet(letterCtx),
		numbers: importSet(numberCtx),
		commons: importSet(commonCtx)
	};
}

// Format sign key names by replacing underscores and capitalizing each word
function formatSignName(key) {
	return key
		.replace(/_/g, ' ')
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

// React component for the Learn Page
function LearnPage() {
	const [selected, setSelected] = useState(''); // Current selected section key
	const navigate = useNavigate(); // React Router navigation
	const location = useLocation(); // Access the location object to retrieve scroll target state
	const topRef = useRef(null); // Unused, may be useful for scroll to top feature

	// Refs for different sections on the page to handle scroll snapping
	const scrollContainerRef = useRef(null);
	const progressRef = useRef(null);
	const letterRef = useRef(null);
	const numberRef = useRef(null);
	const commonRef = useRef(null);

	// Section mapping for scroll detection
	const sections = [
		{ key: 'progress', ref: progressRef },
		{ key: 'letter', ref: letterRef },
		{ key: 'number', ref: numberRef },
		{ key: 'common', ref: commonRef },
	];

	// Load all sign images
	const { letters, numbers, commons } = importAllSigns();

	// Track which signs the user has learned, grouped by category
	const [learntSigns, setLearntSigns] = useState({ letter: [], number: [], common: [] });

	const [isModelLoading, setIsModelLoading] = useState(true); // Loading state for 3D model
	const [showAnimation, setShowAnimation] = useState(false); // Whether to show animation modal
	const [selectedWord, setselectedWord] = useState(null); // Word currently being animated

	// Configuration for the PokoViewer 3D animation component
	const [modelConfig, setModelConfig] = useState({
		scale: 2.2,
		animationSpeed: 0.6,
		currentAnimation: 'Letter dummy',
		isPlayingSequence: false,
		onModelLoaded: () => setIsModelLoading(false)
	});

	const [disableLazyLoad, setDisableLazyLoad] = useState(false);

	// Handles scrolling to a section or back to top if already selected
	const handleScroll = (ref, key) => {
		if (selected === key) {
			setSelected('');
			scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			setSelected(key);
			const targetTop = ref.current?.offsetTop;
			scrollContainerRef.current?.scrollTo({ top: targetTop, behavior: 'smooth' });
		}
	};

	// Toggle the learnt status of a specific sign and persist it to localStorage
	const toggleLearnt = (category, key) => {
		const updated = {
			...learntSigns,
			[category]: learntSigns[category].includes(key)
				? learntSigns[category].filter(k => k !== key)
				: [...learntSigns[category], key],
		};
		setLearntSigns(updated);
		localStorage.setItem('learntSigns', JSON.stringify(updated));
	};

	// Render a progress bar showing how many signs have been learned in a category
	const renderProgressBar = (category, total) => (
		<div className="progress-bar-wrapper">
			<span className="progress-label">
				Progress: {learntSigns[category]?.length || 0}/{total}
			</span>
			<div className="progress-bar-track">
				<div
					className="progress-bar-fill"
					style={{ width: `${(learntSigns[category]?.length || 0) / total * 100}%` }}
				></div>
			</div>
		</div>
	);

	// Reset all learnt signs and clear localStorage
	const handleResetLearning = () => {
		localStorage.removeItem('learntSigns');
		setLearntSigns({ letter: [], number: [], common: [] });
	};

	const handlePrint = async () => {
		setDisableLazyLoad(true); // Step 1

		// Step 2: Wait until all images are loaded
		await new Promise((resolve) => {
			const images = document.querySelectorAll('#print-section img');
			let loaded = 0;
			images.forEach((img) => {
				if (img.complete) {
					loaded++;
					if (loaded === images.length) resolve();
				} else {
					img.onload = img.onerror = () => {
						loaded++;
						if (loaded === images.length) resolve();
					};
				}
			});
		});

		// Step 3: Use html2canvas
		const printSection = document.getElementById('print-section');
		if (!printSection) return;

		const canvas = await html2canvas(printSection, {
			scale: 2,
			useCORS: true,
			backgroundColor: '#ffffff',
		});

		const imgData = canvas.toDataURL('image/png');
		const imgWidth = canvas.width * 0.264583;
		const imgHeight = canvas.height * 0.264583;

		const pdf = new jsPDF({
			orientation: 'portrait',
			unit: 'mm',
			format: [imgWidth, imgHeight],
		});

		pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
		pdf.save('sign-progress.pdf');

		// Step 4: Optionally re-enable lazy loading
		setDisableLazyLoad(false);
	};


	// Renders modal for watching sign animations
	const AnimationModal = ({ isVisible, selectedWord }) => {
		if (!selectedWord) return null;

		let category = '';
		if (letters[selectedWord]) category = 'Letter';
		else if (numbers[selectedWord]) category = 'Number';
		else if (commons[selectedWord]) category = 'Common';
		else return null;

		const isLearnt = learntSigns[category.toLowerCase()].includes(selectedWord);
		const toggleFamiliarity = () => {
			toggleLearnt(category.toLowerCase(), selectedWord);
		};

		// Determine image to display in modal
		const imageSrc =
			category === 'Common' ? commons[selectedWord] :
				category === 'Letter' ? letters[selectedWord] :
					numbers[selectedWord];

		return (
			<div className="instructions-modal-overlay" style={{ display: isVisible ? 'flex' : 'none' }} onClick={() => setShowAnimation(false)}>
				<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
					<button className="close-button" onClick={() => setShowAnimation(false)}>×</button>
					<div className="column-wrapper">
						<h3>{category}: {formatSignName(selectedWord)}</h3>
						<p>Watch closely and try to copy it! 👀✋</p>
						{isModelLoading && (
							<div className="loading-indicator">
								<div className="loading-spinner"></div>
								<p>Loading 3D model...</p>
							</div>
						)}
						<div className="model-container">
							<PokoViewer config={modelConfig} />
						</div>
						<label className="checkbox-wrapper">
							<input
								type="checkbox"
								checked={isLearnt}
								onChange={toggleFamiliarity}
							/>
							<span>I know this sign</span>
						</label>
					</div>
				</div>
			</div>
		);
	};

	// Determine the animation name for a character
	const getAnimationForChar = (char, isFirstInSequence) => {
		if (!char) return 'Letter dummy';
		if (availableNumbers.includes(char)) return `Number ${char}`;
		if (availableLetters.includes(char.toUpperCase())) return `Letter ${char.toUpperCase()}`;
		return isFirstInSequence ? 'Number 0' : 'Letter dummy';
	};

	// Handle click on a letter/number sign
	const handleSignClick = (char) => {
		setselectedWord(char);
		setShowAnimation(true);
		setModelConfig(prev => ({
			...prev,
			currentAnimation: getAnimationForChar(char, false),
			isPlayingSequence: false
		}));
	};

	// Handle click on a common word sign
	const handleWordClick = (word) => {
		setselectedWord(word);
		setShowAnimation(true);
		console.log('word:', word);
		console.log('word from list:', getCommonWordAnimation(word));
		if (hasCommonWordAnimation(word)) {
			setModelConfig(prev => ({
				...prev,
				currentAnimation: getCommonWordAnimation(word),
				isPlayingSequence: false
			}));
		}
	};

	// Load learnt signs from localStorage when component mounts
	useEffect(() => {
		const stored = localStorage.getItem('learntSigns');
		if (stored) {
			try {
				setLearntSigns(JSON.parse(stored));
			} catch (err) {
				console.error('Failed to parse learntSigns from localStorage:', err);
			}
		}
	}, []);

	// On first load, scroll to the section specified in location state (if any)
	useEffect(() => {
		if (location.state?.scrollTo) {
			// Find the matching section by key
			const target = sections.find(sec => sec.key === location.state.scrollTo);
			if (target?.ref?.current) {
				// Scroll smoothly to the target section
				scrollContainerRef.current?.scrollTo({
					top: target.ref.current.offsetTop,
					behavior: 'smooth'
				});
				// Optionally highlight the selected section
				setSelected(location.state.scrollTo);
			}
		}
	}, [location.state, sections]);

	// Update selected section on scroll
	useEffect(() => {
		const onScroll = () => {
			const scrollPosition = scrollContainerRef.current.scrollTop;
			const visibleSection = sections.find(({ ref }) => {
				if (!ref.current) return false;
				const offsetTop = ref.current.offsetTop;
				const offsetHeight = ref.current.offsetHeight;
				return (
					scrollPosition >= offsetTop - 50 &&
					scrollPosition < offsetTop + offsetHeight - 50
				);
			});

			if (visibleSection && visibleSection.key !== selected) {
				setSelected(visibleSection.key);
			}
		};

		const container = scrollContainerRef.current;
		container?.addEventListener('scroll', onScroll);

		return () => {
			container?.removeEventListener('scroll', onScroll);
		};
	}, [selected, sections]);

	return (
		<div className="learn-page2 page">
			<div className="white-wrapper container">
				<AnimationModal
					isVisible={showAnimation}
					selectedWord={selectedWord}
				/>
				{/* {showAnimation && <AnimationModal word={selectedWord} />} */}
				<div className="select-section">
					<div className="option-buttons">
						<button className={selected === 'progress' ? 'active' : ''} onClick={() => handleScroll(progressRef, 'progress')}>
							📈 My Progress
						</button>
					</div>
					<p><strong>✨ Pick a category to learn:</strong></p>
					<div className="option-buttons">
						<button className={selected === 'letter' ? 'active' : ''} onClick={() => handleScroll(letterRef, 'letter')}>
							🔤 Letters
						</button>
						<button className={selected === 'number' ? 'active' : ''} onClick={() => handleScroll(numberRef, 'number')}>
							🔢 Numbers
						</button>
						<button className={selected === 'common' ? 'active' : ''} onClick={() => handleScroll(commonRef, 'common')}>
							💬 Common Signs
						</button>
					</div>
				</div>

				<div className="scroll-content" ref={scrollContainerRef}>
					<div className="page-title" ref={topRef}>
						<h1>📘 Let's Learn Signs</h1>
						<h5>Explore different sign categories and start learning today!</h5>
					</div>

					<div className="speech-section">
						<div className="speech-bubble-left">
							<p><strong>Explore and learn!</strong><br />This is your special sign-language book! 🧠✨</p>
							<p className='with-select'>👈 Tap a category to see how it’s signed.</p>
							<p className='without-select'>👇 Scroll down to start your sign learning journey!</p>
							<p>🎬 Click a sign card to watch the sign in action!</p>
							<p>✅ Tick the box next to each sign to track your learning progress!</p>
						</div>
						<img src={poko_read} alt="Buddy waving" className="poko" />
					</div>
					<div id='print-section'>
						<div className='content-box' ref={progressRef}>
							<div className='row-wrapper'>
								<h4><strong>📈 My Learning Progress</strong></h4>
								<div>
									<button className='print-btn' onClick={handlePrint}>🖨️ Print Signs</button>
									<button className="reset-btn" onClick={handleResetLearning}>🔁 Reset My Progress</button>
								</div>
							</div>
							<div className='progress-grid'>
								<div className='progress-wrapper' onClick={() => handleScroll(letterRef, 'letter')}>
									<h5>🔤 Letters (A-Z)</h5>
									{renderProgressBar('letter', Object.keys(letters).length)}
								</div>
								<div className='progress-wrapper' onClick={() => handleScroll(numberRef, 'number')}>
									<h5>🔢 Numbers (0-9)</h5>
									{renderProgressBar('number', Object.keys(numbers).length)}
								</div>
								<div className='progress-wrapper' onClick={() => handleScroll(commonRef, 'common')}>
									<h5>💬 Common Signs</h5>
									{renderProgressBar('common', Object.keys(commons).length)}
								</div>
							</div>
						</div>
						<div className="content-box" ref={letterRef}>
							<div className='row-wrapper'>
								<h4><strong>🔤 Letters (A-Z)</strong></h4>
								{renderProgressBar('letter', Object.keys(letters).length)}
							</div>
							<div className="content sign-grid">
								{Object.entries(letters).map(([key, src]) => (
									<div key={key} className="sign-card" onClick={() => handleSignClick(key)}>
										<label className="checkbox-wrapper">
											<input
												type="checkbox"
												checked={learntSigns.letter.includes(key)}
												onChange={() => toggleLearnt('letter', key)}
												onClick={(e) => e.stopPropagation()}
											/>
										</label>
										<h2 className='key'>{key.toUpperCase()}</h2>
										{disableLazyLoad ? (
											<img src={src} alt={key} className="print-sign-img" />
										) : (
											<LazyLoadImage
												src={src}
												alt={key}
												effect="blur"
												className="sign-img"
											/>
										)}
									</div>
								))}
							</div>
						</div>

						<div className="content-box" ref={numberRef}>
							<div className='row-wrapper'>
								<h4><strong>🔢 Numbers (0-9)</strong></h4>
								{renderProgressBar('number', Object.keys(numbers).length)}
							</div>
							<div className="content sign-grid">
								{Object.entries(numbers).map(([key, src]) => (
									<div key={key} className="sign-card" onClick={() => handleSignClick(key)}>
										<label className="checkbox-wrapper">
											<input
												type="checkbox"
												checked={learntSigns.number.includes(key)}
												onChange={() => toggleLearnt('number', key)}
												onClick={(e) => e.stopPropagation()}
											/>
										</label>
										<h2 className='key'>{key}</h2>
										{disableLazyLoad ? (
											<img src={src} alt={key} className="print-sign-img" />
										) : (
											<LazyLoadImage
												src={src}
												alt={key}
												effect="blur"
												className="sign-img"
											/>
										)}
									</div>
								))}
							</div>
						</div>


						<div className="content-box" ref={commonRef}>
							<div className='row-wrapper'>
								<h4><strong>💬 Common Signs</strong></h4>
								{renderProgressBar('common', Object.keys(commons).length)}
							</div>
							<div className="content sign-grid">
								{Object.entries(commons).map(([key, src]) => (
									<div key={key} className="sign-card" onClick={() => handleWordClick(key)}>
										<label className="checkbox-wrapper">
											<input
												type="checkbox"
												checked={learntSigns.common.includes(key)}
												onChange={() => toggleLearnt('common', key)}
												onClick={(e) => e.stopPropagation()}
											/>
										</label>
										<h5 className='key'>{formatSignName(key)}</h5>
										{disableLazyLoad ? (
											<img src={src} alt={key} className="print-sign-img" />
										) : (
											<LazyLoadImage
												src={src}
												alt={key}
												effect="blur"
												className="sign-img"
											/>
										)}
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			<NavigationButtons />
		</div>
	);
}

export default LearnPage;
